<template>
  <div class="space-y-2">
    <div class="flex items-center space-x-2 min-h-16 cursor-pointer" @click="() => { event.isAnonymous && !event.isOrganizing(currentUser?._id) ? false : isActive = !isActive }">
      <div class="max-w-24 -space-x-4 flex items-center" v-if="organizers.length > 0">
        <UserPicture :user="organizer" is-event v-for="organizer in organizers.slice(0, 2)" is-link size="base" :is-hide-badges="organizers.length > 1" class="shrink-0" />
      </div>

      <div class="grow">
        <p class="font-bold text-base leading-tight -mt-0.5">{{ $t('components.Event.page.organizedBy', { name: organizers.length > 0 ? getNameList(organizers.map(o => o.name), { max: 2, and: $t('common.and'), others: $t('common.others') }) : organization?.name ?? '' }) }}</p>

        <div class="flex items-center space-x-2 mt-1">
          <div class="flex items-center -space-x-0.5 min-h-5" v-if="((attendees.length > 1 || saved.length <= 0) ? attendees : saved).length > 0">
            <UserPicture
              size="3xs"
              is-hide-badges
              :user="attendee"
              :is-anonymous="event.isAnonymous"
              v-for="attendee in ((attendees.length > 1 || saved.length <= 0) ? attendees : saved).slice(0, 5)" :key="attendee._id"
            />
            
            <div
              v-if="isPending"
              v-for="i in 5"
              :key="i"
              class="size-5 rounded-full bg-white opacity-10"
            />
          </div>

          <div class="font-medium text-xs leading-none line-clamp-1 break-all" v-if="((attendees.length > 1 || saved.length <= 0) ? attendees : saved).length > 0">
            {{ [ ...(event.attendeeCount > 1 ? [ $t('common.goingCount', event.attendeeCount) ] : []), ...(waiting.length > 0 ? [ $t('components.Event.waitingCount', waiting.length) ] : []), ...(saved.length > 0 && waiting.length <= 0 ? [ $t('common.savedCount', saved.length) ] : []) ].join(' · ') }} 
          </div>
          <div class="font-medium text-sm leading-none line-clamp-1 break-all opacity-60" v-else>
            {{ $t('components.Event.first') }}
          </div>
        </div>
      </div>

      <div class="shrink-0">
        <IconBase name="cute/lock-fill" v-if="event.isAnonymous && !event.isOrganizing(currentUser?._id)" />
        <IconBase name="cute/angle-right" :class="[ isActive ? 'rotate-90' : '']" v-else />
      </div>
    </div>

    <Transition name="fade">
      <div class="space-y-3 mt-4 text-shadow-none bg-white dark:bg-dark-800 dark:text-dark-50 text-light-950 px-3 py-3 rounded-3xl" v-if="isActive">
        <div class="-mx-1" v-if="attendees.length > 0">
          <div class="px-1 font-bold mb-1 w-full flex items-center space-x-0.5">
            <ChipRound size="xs">{{ event.attendeeCount }}</ChipRound>
            <p>{{ $t('components.Event.registered') }}</p>
          </div>

          <div class="space-y-1 mt-3" v-if="event.isOrganizing(currentUser?._id)">
            <UserCard :user="user" size="base" :event="event" class="border border-charcoal-100 dark:bg-dark-700 dark:border-none rounded-md p-2 flex items-center" v-for="user in attendees" :context="['user-event', 'user']" :key="user._id" />
          </div>
          <div class="flex flex-wrap" v-else>
            <div v-for="user in attendees" :key="user._id" class="m-1 relative">
              <UserPicture size="md" is-link :user="user" />

              <div class="size-5 rounded-full bg-charcoal-950 flex justify-center items-center text-white absolute text-4xs font-bold -left-1 -top-0.5 z-10" v-if="(event?.attendees ?? []).find(a => a._id === user._id)?.guests ?? 0 > 0">
                +{{ (event?.attendees ?? []).find(a => a._id === user._id)?.guests ?? 0 }}
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap p-2 -mx-1 !-mb-1 bg-royal-200/60 dark:bg-royal-600/20 rounded-2xl" v-if="waiting.length > 0">
          <div class="font-bold mb-1 w-full flex items-center space-x-0.5">
            <ChipRound size="xs">{{ event.waitingCount }}</ChipRound>
            <p>{{ $t('components.Event.waitlist') }}</p>
          </div>
          <div v-for="user in waiting" :key="user._id" class="m-1 relative">
            <UserPicture size="base" is-link :user="user" />

            <div class="size-5 rounded-full bg-charcoal-950 flex justify-center items-center text-white absolute text-4xs font-bold -left-1 -top-0.5 z-10" v-if="(event?.attendees ?? []).find(a => a._id === user._id)?.guests ?? 0 > 0">
                +{{ (event?.attendees ?? []).find(a => a._id === user._id)?.guests ?? 0 }}
              </div>
          </div>
        </div>

        <div class="flex flex-wrap p-2 -mx-1 !-mb-1 bg-charcoal-100 dark:bg-dark-700 rounded-2xl" v-if="saved.length > 0">
          <div class="font-bold mb-2 w-full flex items-center space-x-0.5">
            <ChipRound size="xs">{{ saved.length }}</ChipRound>
            <p>{{ $t('components.Event.saved') }}</p>
          </div>
          <div v-for="user in saved" :key="user._id" class="m-0.5">
            <UserPicture size="sm" is-link :user="user" />
          </div>
        </div>
      </div>
    </Transition>

    <div class="p-4 bg-whale-950 text-whale-50 rounded-2xl text-shadow-none" v-if="event.isAnonymous">
      <div class="font-bold text-lg flex items-center space-x-1.5">
        <ImageEmoji name="Ghost" size="sm" class="size-6 shrink-0" />
        <p class="grow">{{ $t('components.Event.anonymous.title') }}</p>
      </div>

      <TextHider2 :lines="2" class="font-medium mt-1.5" bg="bg-whale-950" gradient="from-whale-950/0 to-whale-950">
        {{ $t('components.Event.anonymous.content') }}
      </TextHider2>

      <div class="grid grid-cols-3 gap-1 mt-4" v-if="event.stats">
        <div class="text-center flex flex-col items-center justify-center bg-whale-900 rounded-xl py-4">
          <p class="font-bold text-2xl !leading-none">{{ Math.floor(event.stats.age) }}</p>
          <p class="text-xs font-medium">{{ $t('components.Event.stats.age') }}</p>
        </div>

        <div class="text-center grid col-span-2 grid-cols-3 px-2 bg-whale-900 rounded-xl">
          <div class="flex flex-col items-center justify-center space-y-0.5 py-2">
            <p class="font-bold text-md !leading-none">{{ Math.round(event.stats.genders.man * 100) }}%</p>
            <p class="text-xs font-medium leading-none">{{ $t('components.User.genders.man.plural').toLowerCase() }}</p>
          </div>
          <div class="flex flex-col items-center justify-center space-y-0.5 py-2">
            <p class="font-bold text-md !leading-none">{{ Math.round(event.stats.genders.woman * 100) }}%</p>
            <p class="text-xs font-medium leading-none">{{ $t('components.User.genders.woman.plural').toLowerCase() }}</p>
          </div>
          <div class="flex flex-col items-center justify-center space-y-0.5 py-2">
            <p class="font-bold text-md !leading-none">{{ Math.round(event.stats.genders.nb * 100) }}%</p>
            <p class="text-xs font-medium leading-none">{{ $t('components.User.genders.nb.plural').toLowerCase() }}</p>
          </div>
        </div>

        <div class="text-center grid col-span-3 grid-cols-3 px-2 bg-whale-900 rounded-xl">
          <div class="flex flex-col items-center justify-center space-y-0.5 py-4">
            <p class="font-bold text-2xl !leading-none">{{ Math.round(event.stats.relationship.single * 100) }}%</p>
            <p class="text-xs font-medium leading-none">{{ $t('components.User.relationship.single').toLowerCase() }}</p>
          </div>
          <div class="flex flex-col items-center justify-center space-y-0.5 py-4">
            <p class="font-bold text-2xl !leading-none">{{ Math.round(event.stats.relationship.couple * 100) }}%</p>
            <p class="text-xs font-medium leading-none">{{ $t('components.User.relationship.relationship').toLowerCase() }}</p>
          </div>
          <div class="flex flex-col items-center justify-center space-y-0.5 py-4">
            <p class="font-bold text-2xl !leading-none">{{ Math.round(event.stats.relationship.hide * 100) }}%</p>
            <p class="text-xs font-medium leading-none">
              {{ $t('components.User.relationship.unspecified').toLowerCase() }}
            </p>
          </div>
        </div>

        <div class="col-span-3 p-4 bg-whale-900 rounded-xl">
          <p class="font-medium text-sm">{{ $t('components.Event.stats.met', event.stats.met) }}</p>
          <p class="font-medium text-sm">{{ $t('components.Event.stats.following', event.stats.following) }}</p>
          <p class="font-medium text-sm">{{ $t('components.Event.stats.blocked', event.stats.blocked) }}</p>
        </div>
      </div>
    </div>

    <PopinBase :is-active="route.query.kick && userKicking ? true : false" size="3xs" color="ruby" @close="onKickClose">
      <template #body v-if="userKicking">
        <div class="p-5 text-ruby-950">
          <p class="font-bold text-md">{{ $t('components.Event.kick.title', { name: userKicking.name })}}</p>

          <p class="font-medium mt-2 mb-4">{{ $t('components.Event.kick.content') }}</p>

          <InputTags2
            v-model="formData.reasons"
            :options="['no-answer', 'mixity', 'empty-profile', 'fiability', 'experience', 'request'].map(r => ({ value: r, label: $t(`components.Event.kick.reasons.${r}`) }))"
          />

          <p class="font-medium mt-4 mb-2 text-sm">
            {{ $t('components.Event.kick.details', { name: userKicking.name }) }}</p>

          <InputTextarea class="p-4 rounded-xl" :rows="4" v-model="formData.details" :placeholder="$t('components.Event.kick.placeholder')" />
        </div>
      </template>
      <template #footer>
        <div class="px-2 pb-2">
          <ButtonBase @click="onKick" :is-disabled="formData.reasons.length <= 0 || formData.details.length < 10" is-full>
            {{ $t('components.Event.kick.action') }}
          </ButtonBase>
        </div>
      </template>
    </PopinBase>
  </div>
</template>

<script lang="ts" setup>
import UsersStore from '@/store/users'
import UserStore from '@/store/user'
import OrganizationStore from '@/store/organization'
import EventsStore from '@/store/events'
import { Event } from '@/classes'
import { eventAttendKickAPI } from '~/api/event'

const props = defineProps<{
  event: Event,
  isPending?: boolean
}>()

const usersStore = UsersStore()
const userStore = UserStore()
const eventStore = EventsStore()
const organizationStore = OrganizationStore()
const { currentUser } = storeToRefs(userStore)

const isActive = ref(false)
const router = useRouter()
const route = useRoute()

const organizers = computed(() => usersStore.getItems(props.event.organizers.map(o => o._id)))

const organization = computed(() => props.event.organization ? organizationStore.getItem(props.event.organization) : null)

const attendees = computed(() => usersStore.getItems([
  ...new Set([
    ...props.event.organizers.map(o => o._id),
    ...props.event.attendingUsers.sort((a, b) => Number(b.createdAt) - Number(a.createdAt)).map(a => a._id) || []
  ])
]))

const waiting = computed(() => usersStore.getItems(props.event.waitingUsers.sort((a, b) => Number(b.createdAt) - Number(a.createdAt)).map(a => a._id) || []))

const saved = computed(() => usersStore.getItems((props.event.interestedUsers ?? [])))


// KICK

const userKicking = computed(() => usersStore.getItem(route.query.kick as string))
const formData = ref({
  reasons: [],
  details: ''
})

const isLoading = ref(false)

const onKick = async () => {
  if (!userKicking.value) return

  isLoading.value = true

  const response = await eventAttendKickAPI(props.event._id, userKicking.value._id, formData.value)

  if (response.ok) {
    if (response.data.updatedEvent) eventStore.setItem(response.data.updatedEvent, { priority: 5 })

    onKickClose()
  }
  
  isLoading.value = false
}

const onKickClose = () => {
  formData.value = { reasons: [], details: '' }
  router.push({ query: { ...route.query, kick: undefined } })
}
</script>